import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Clock,
  Paperclip,
  Play,
  Printer,
  Plus,
  QrCode,
  Search,
  UserPlus,
  Wrench,
} from "lucide-react";
import { downloadProtected, request } from "./FunctionalApp";

const label = (value: string = "") =>
  value
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (x) => x.toUpperCase());
const when = (value?: string) =>
  value ? new Date(value).toLocaleString() : "—";
async function json(path: string, init: RequestInit = {}) {
  const response = await request(path, init);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(label(data.error || "Unable to save"));
  return data;
}
const post = (path: string, body: any = {}) =>
  json(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });

export function MaintenanceWorkOrders({ initialId }: { initialId?: string }) {
  const [data, setData] = useState<any>({
      orders: [],
      users: [],
      assets: [],
      canAssign: false,
    }),
    [selected, setSelected] = useState(""),
    [detail, setDetail] = useState<any>(null),
    [query, setQuery] = useState(""),
    [status, setStatus] = useState("ACTIVE"),
    [message, setMessage] = useState(""),
    [creating, setCreating] = useState(false);
  async function load(keep = selected) {
    try {
      const next = await json("/api/maintenance/work-orders");
      setData(next);
      if (keep) setDetail(await json(`/api/maintenance/work-orders/${keep}`));
    } catch (e: any) {
      setMessage(e.message);
    }
  }
  useEffect(() => {
    load(initialId || "");
  }, [initialId]);
  const rows = useMemo(
    () =>
      data.orders.filter(
        (x: any) =>
          (status === "ALL" ||
            (status === "ACTIVE"
              ? !["COMPLETED", "CANCELLED"].includes(x.status)
              : x.status === status)) &&
          `${x.number} ${x.title} ${x.asset_name || ""} ${x.discipline}`
            .toLowerCase()
            .includes(query.toLowerCase()),
      ),
    [data.orders, query, status],
  );
  async function open(workOrderId: string) {
    setSelected(workOrderId);
    try {
      setDetail(await json(`/api/maintenance/work-orders/${workOrderId}`));
    } catch (e: any) {
      setMessage(e.message);
    }
  }
  async function action(path: string, body: any = {}) {
    try {
      const result = await post(path, body);
      setMessage(result.message || "Work order updated");
      await load(selected);
      return true;
    } catch (e: any) {
      setMessage(e.message);
      return false;
    }
  }
  if (detail)
    return (
      <WorkOrderDetail
        value={detail}
        users={data.users}
        jobTypes={data.jobTypes || []}
        canAssign={data.canAssign}
        back={() => {
          setDetail(null);
          setSelected("");
        }}
        action={action}
        message={message}
      />
    );
  return (
    <>
      <div className="func-heading">
        <div>
          <h1>Work orders</h1>
          <p>Report, claim, repair and sign off maintenance work.</p>
        </div>
        <button onClick={() => setCreating(!creating)}>
          <Plus />
          New work order
        </button>
      </div>
      {message ? <div className="func-admin-note">{message}</div> : null}
      {creating ? (
        <NewWorkOrder
          assets={data.assets}
          jobTypes={data.jobTypes || []}
          saved={async (workOrderId) => {
            setCreating(false);
            await load(workOrderId);
            await open(workOrderId);
          }}
        />
      ) : null}
      <section className="func-panel cmms-list">
        <div className="func-table-head">
          <h2>Maintenance queue</h2>
          <div className="cmms-filters">
            <label>
              <Search />
              <input
                placeholder="Number, issue, asset or trade"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="ACTIVE">Active</option>
              <option value="ALL">All</option>
              <option value="OPEN">Open</option>
              <option value="IN_PROGRESS">In progress</option>
              <option value="TEMPORARY_FIX">Temporary fix</option>
              <option value="AWAITING_SIGNOFF">Awaiting sign-off</option>
              <option value="COMPLETED">Completed</option>
            </select>
          </div>
        </div>
        <div className="cmms-orders">
          {rows.map((w: any) => (
            <button key={w.id} onClick={() => open(w.id)}>
              <span>
                <strong>{w.number}</strong>
                <em className={`cmms-priority p-${w.priority}`}>
                  {label(w.priority)}
                </em>
              </span>
              <span>
                <b>{w.title}</b>
                <small>
                  {w.asset_name || "No asset"} ·{" "}
                  {w.job_type_name || "Work order"}
                  {w.contractor ? " · Contractor" : ""} · {label(w.discipline)}
                </small>
              </span>
              <span>
                <em className={`cmms-status s-${w.status}`}>
                  {label(w.status)}
                </em>
                <small>
                  {w.assigned_to_name || w.claimed_by_name || "Unassigned"}
                </small>
              </span>
              <span>
                <small>Reported</small>
                {when(w.reported_at)}
              </span>
            </button>
          ))}
        </div>
        {!rows.length ? (
          <p className="func-empty">No matching work orders.</p>
        ) : null}
      </section>
    </>
  );
}

function NewWorkOrder({
  assets,
  jobTypes,
  saved,
}: {
  assets: any[];
  jobTypes: any[];
  saved: (id: string) => void;
}) {
  const [f, setF] = useState({
      title: "",
      description: "",
      priority: "MEDIUM",
      assetId: "",
      jobTypeId: jobTypes[0]?.id || "",
    }),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await post("/api/maintenance/work-orders", {
        ...f,
        assetId: f.assetId || null,
      });
      saved(result.id);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="func-panel cmms-create">
      <div className="func-table-head">
        <h2>Report maintenance work</h2>
      </div>
      <form onSubmit={submit}>
        <label>
          Issue title
          <input
            required
            value={f.title}
            onChange={(e) => setF({ ...f, title: e.target.value })}
          />
        </label>
        <label>
          Asset
          <select
            value={f.assetId}
            onChange={(e) => setF({ ...f, assetId: e.target.value })}
          >
            <option value="">No asset selected</option>
            {assets.map((a) => (
              <option key={a.id} value={a.id}>
                {a.path || a.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Job type
          <select
            required
            value={f.jobTypeId}
            onChange={(e) => setF({ ...f, jobTypeId: e.target.value })}
          >
            {jobTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
                {type.contractor ? " — contractor" : ""}
              </option>
            ))}
          </select>
        </label>
        <label>
          Priority
          <select
            value={f.priority}
            onChange={(e) => setF({ ...f, priority: e.target.value })}
          >
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
            <option value="PRODUCTION_CAN_RUN">Production can run</option>
            <option value="PRODUCTION_STOPPED">Production stopped</option>
          </select>
        </label>
        <label className="wide">
          Description
          <textarea
            value={f.description}
            onChange={(e) => setF({ ...f, description: e.target.value })}
          />
        </label>
        {error ? <p className="cmms-error">{error}</p> : null}
        <button disabled={busy}>
          {busy ? "Creating…" : "Create work order"}
        </button>
      </form>
    </section>
  );
}

function WorkOrderDetail({
  value,
  users,
  jobTypes,
  canAssign,
  back,
  action,
  message,
}: any) {
  const w = value.order,
    [note, setNote] = useState(""),
    [minutes, setMinutes] = useState(""),
    [description, setDescription] = useState(""),
    [temporaryForm, setTemporaryForm] = useState(false),
    [followUpForm, setFollowUpForm] = useState(false),
    [actionForm, setActionForm] = useState<any>(null),
    [checklist, setChecklist] = useState<any>({ items: [], complete: true }),
    [responses, setResponses] = useState<Record<string,string>>({});
  const canSignoff = Boolean(value.permissions?.canSignoff),
    canComplete = Boolean(value.permissions?.canComplete);
  const run = (suffix: string, body: any = {}) =>
    action(`/api/maintenance/work-orders/${w.id}/${suffix}`, body);
  async function loadChecklist() {
    try {
      setChecklist(
        await json(`/api/maintenance/work-orders/${w.id}/checklist`),
      );
    } catch {}
  }
  useEffect(() => {
    loadChecklist();
  }, [w.id]);
  async function toggleChecklist(item: any) {
    try {
      await post(`/api/maintenance/work-orders/${w.id}/checklist/${item.id}`, {
        completed: !item.completed_at,
        responseValue: responses[item.id] ?? item.response_value ?? '',
      });
      await loadChecklist();
    } catch (e: any) {
      alert(e.message);
    }
  }
  function openAction(
    suffix: string,
    title: string,
    key = "reason",
    fieldLabel = "Details",
    placeholder = "Add enough detail for the next person to understand the action.",
  ) {
    setActionForm({ suffix, title, key, fieldLabel, placeholder });
  }
  async function attach(file: File) {
    if (file.size > 2_000_000) return alert("Maximum file size is 2 MB.");
    const reader = new FileReader();
    reader.onload = () =>
      run("attachments", {
        name: file.name,
        mimeType: file.type,
        url: reader.result,
      });
    reader.readAsDataURL(file);
  }
  async function printSheet() {
    const popup = window.open("", "_blank");
    if (popup)
      popup.document.write(
        "<p style='font-family:sans-serif;padding:24px'>Preparing work order…</p>",
      );
    try {
      const response = await request(
        `/api/maintenance/work-orders/${w.id}/print`,
      );
      if (!response.ok) throw new Error("Unable to generate work order");
      const url = URL.createObjectURL(await response.blob());
      if (popup) popup.location.href = url;
      else window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error: any) {
      popup?.close();
      alert(error.message);
    }
  }
  return (
    <>
      <div className="cmms-detail-head">
        <button className="secondary" onClick={back}>
          <ArrowLeft />
          Queue
        </button>
        <div>
          <span>{w.number}</span>
          <h1>{w.title}</h1>
          <p>
            {w.asset_name || "No asset"} · {label(w.discipline)} · Reported{" "}
            {when(w.reported_at)}
          </p>
        </div>
        <em className={`cmms-status s-${w.status}`}>{label(w.status)}</em>
        <button
          className="secondary"
          onClick={() =>
            downloadProtected(
              `/api/cmms/work-orders/${w.id}/qr.svg`,
              `${w.number}-qr.svg`,
            )
          }
        >
          <QrCode /> QR label
        </button>
        {canAssign ? (
          <button className="secondary" onClick={printSheet}>
            <Printer /> Print job sheet
          </button>
        ) : null}
        {canAssign && w.job_type_applies_to === "PPM" ? (
          <button className="secondary" onClick={() => setFollowUpForm(true)}>
            <Plus /> Create follow-up
          </button>
        ) : null}
      </div>
      {message ? <div className="func-admin-note">{message}</div> : null}
      <div className="cmms-detail-grid">
        <section className="func-panel cmms-main">
          <h2>Work order</h2>
          <p>{w.description || "No description supplied."}</p>
          <dl>
            <div>
              <dt>Job type</dt>
              <dd>
                {w.job_type_name || "Work order"}
                {w.contractor ? " · Contractor" : ""}
              </dd>
            </div>
            <div>
              <dt>Priority</dt>
              <dd>{label(w.priority)}</dd>
            </div>
            <div>
              <dt>Trade</dt>
              <dd>{label(w.discipline)}</dd>
            </div>
            <div>
              <dt>Claimed by</dt>
              <dd>{w.claimed_by_name || "—"}</dd>
            </div>
            <div>
              <dt>Assigned to</dt>
              <dd>{w.assigned_to_name || "—"}</dd>
            </div>
            <div>
              <dt>Labour</dt>
              <dd>
                {value.labour.reduce((n: number, x: any) => n + x.minutes, 0)}{" "}
                minutes
              </dd>
            </div>
            <div>
              <dt>Production restored</dt>
              <dd>{when(w.production_restored_at)}</dd>
            </div>
          </dl>
          {w.temporary_fix ? (
            <div className="cmms-repair">
              <b>Temporary fix</b>
              <p>{w.temporary_fix}</p>
              <b>Permanent work still required</b>
              <p>{w.temporary_fix_work_required || "Not recorded"}</p>
              <b>Parts required</b>
              <p>{w.temporary_fix_parts_required || "None recorded"}</p>
              <small>
                Permanent repair due {when(w.permanent_repair_due_at)}
              </small>
            </div>
          ) : null}
          {w.permanent_repair ? (
            <div className="cmms-repair permanent">
              <b>Permanent repair</b>
              <p>{w.permanent_repair}</p>
            </div>
          ) : null}
          {checklist.items.length ? (
            <div className="cmms-checklist">
              <h2>PPM checklist</h2>
              <p>
                {checklist.complete
                  ? "All mandatory steps completed."
                  : "Complete every mandatory step before recording the permanent repair."}
              </p>
              {checklist.items.map((item: any) => (
                <label key={item.id}>
                  {item.response_type==='CHECK' ? <input
                    type="checkbox"
                    checked={Boolean(item.completed_at)}
                    disabled={!canComplete}
                    onChange={() => toggleChecklist(item)}
                  /> : null}
                  <span>
                    <b>{item.label}</b>
                    {item.response_type !== 'CHECK' ? <span className="ppm-response"><input type={item.response_type==='NUMBER'?'number':'text'} step={item.response_type==='NUMBER'?'any':undefined} aria-label={item.label} placeholder={item.response_type==='NUMBER'?`Enter value${item.unit ? ` (${item.unit})` : ''}`:'Enter response'} value={responses[item.id] ?? item.response_value ?? ''} disabled={!canComplete || Boolean(item.completed_at)} onChange={e=>setResponses({...responses,[item.id]:e.target.value})}/>{item.unit ? <small>{item.unit}</small> : null}{canComplete ? <button type="button" className="secondary" onClick={()=>toggleChecklist(item)}>{item.completed_at?'Reopen':'Save response'}</button> : null}</span> : null}
                    {item.completed_at ? (
                      <small>
                        Completed by {item.completed_by_name || "engineer"} ·{" "}
                        {when(item.completed_at)}
                      </small>
                    ) : (
                      <small>Required</small>
                    )}
                  </span>
                </label>
              ))}
            </div>
          ) : null}
          <div className="cmms-actions">
            <ActionButtons
              w={w}
              run={run}
              openAction={openAction}
              openClaim={() =>
                setActionForm({ suffix: "claim", title: "Claim work order", discipline: true })
              }
              openTemporaryForm={() => setTemporaryForm(true)}
              canComplete={canComplete}
              canSignoff={canSignoff}
            />
          </div>
          {canAssign ? (
            <div className="cmms-assign">
              <label>
                Assign engineer
                <select
                  defaultValue=""
                  onChange={(e) =>
                    e.target.value && run("assign", { userId: e.target.value })
                  }
                >
                  <option value="">Choose…</option>
                  {users.map((u: any) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Add assistant
                <select
                  defaultValue=""
                  onChange={(e) =>
                    e.target.value &&
                    run("assistants", { userId: e.target.value })
                  }
                >
                  <option value="">Choose…</option>
                  {users.map((u: any) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : null}
          <div className="cmms-assistants">
            <b>Assistants</b>
            {value.assistants.length ? (
              value.assistants.map((a: any) => <span key={a.id}>{a.name}</span>)
            ) : (
              <span>None</span>
            )}
          </div>
          {canComplete ? (
            <label className="func-button cmms-attach">
              <Paperclip />
              Attach photo or PDF
              <input
                hidden
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                onChange={(e) =>
                  e.target.files?.[0] && attach(e.target.files[0])
                }
              />
            </label>
          ) : null}
          <div className="cmms-attachments">
            {value.attachments.map((a: any) => (
              <a
                key={a.id}
                href={a.url}
                download={a.name}
                target="_blank"
                rel="noreferrer"
              >
                {a.name}
              </a>
            ))}
          </div>
        </section>
        <aside>
          <section className="func-panel cmms-entry">
            <h2>Engineer notes</h2>
            {canComplete ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (note) {
                    run("notes", { body: note });
                    setNote("");
                  }
                }}
              >
                <textarea
                  placeholder="Add a factual update…"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <button>Add note</button>
              </form>
            ) : null}
            {value.notes.map((n: any) => (
              <div key={n.id}>
                <b>{n.author_name}</b>
                <small>{when(n.created_at)}</small>
                <p>{n.body}</p>
              </div>
            ))}
          </section>
          <section className="func-panel cmms-entry">
            <h2>Labour time</h2>
            {canComplete ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (minutes) {
                    run("labour", { minutes: Number(minutes), description });
                    setMinutes("");
                    setDescription("");
                  }
                }}
              >
                <input
                  type="number"
                  min="1"
                  max="1440"
                  placeholder="Minutes"
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value)}
                />
                <input
                  placeholder="Work carried out"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
                <button>
                  <Clock />
                  Log time
                </button>
              </form>
            ) : null}
            {value.labour.map((l: any) => (
              <div key={l.id}>
                <b>
                  {l.user_name} · {l.minutes} min
                </b>
                <small>{when(l.created_at)}</small>
                <p>{l.description}</p>
              </div>
            ))}
          </section>
        </aside>
      </div>
      <section className="func-panel cmms-timeline">
        <h2>Audit timeline</h2>
        {value.events.map((e: any) => (
          <div key={e.id}>
            <i />
            <span>
              <b>{label(e.event)}</b>
              <small>
                {e.actor_name} · {when(e.created_at)}
              </small>
            </span>
            <em>
              {e.from_status ? `${label(e.from_status)} → ` : ""}
              {label(e.to_status)}
            </em>
          </div>
        ))}
      </section>
      {temporaryForm ? (
        <TemporaryRepairForm
          close={() => setTemporaryForm(false)}
          save={async (body: any, photo?: File) => {
            const saved = await run("temporary-fix", body);
            if (!saved) return;
            if (photo) await attach(photo);
            setTemporaryForm(false);
          }}
        />
      ) : null}
      {actionForm ? (
        <WorkOrderActionForm
          config={actionForm}
          close={() => setActionForm(null)}
          save={async (body: any) => {
            const saved = await run(actionForm.suffix, body);
            if (saved !== false) setActionForm(null);
          }}
        />
      ) : null}
      {followUpForm ? (
        <PpmFollowUpForm
          workOrder={w}
          jobTypes={jobTypes}
          close={() => setFollowUpForm(false)}
          saved={(number: string) => {
            setFollowUpForm(false);
            alert(`${number} created and linked to ${w.number}.`);
          }}
        />
      ) : null}
    </>
  );
}

function WorkOrderActionForm({ config, close, save }: any) {
  const [value, setValue] = useState("");
  const [discipline, setDiscipline] = useState("MECHANICAL");
  const [busy, setBusy] = useState(false);
  return (
    <div className="cmms-modal" role="dialog" aria-modal="true">
      <form
        className="cmms-temp-form"
        onSubmit={async (event: FormEvent) => {
          event.preventDefault();
          setBusy(true);
          await save(config.discipline ? { discipline } : { [config.key]: value.trim() });
          setBusy(false);
        }}
      >
        <div>
          <h2>{config.title}</h2>
          <p>This action is recorded in the work order audit timeline.</p>
        </div>
        {config.discipline ? (
          <label>
            Engineering discipline
            <select value={discipline} onChange={(e) => setDiscipline(e.target.value)}>
              <option value="GENERAL">General</option>
              <option value="MECHANICAL">Mechanical</option>
              <option value="ELECTRICAL">Electrical</option>
            </select>
          </label>
        ) : (
          <label>
            {config.fieldLabel}
            <textarea
              autoFocus
              required
              minLength={3}
              rows={5}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={config.placeholder}
            />
          </label>
        )}
        <div className="cmms-modal-actions">
          <button type="button" className="secondary" onClick={close}>Cancel</button>
          <button disabled={busy}>{busy ? "Saving…" : "Confirm action"}</button>
        </div>
      </form>
    </div>
  );
}

function PpmFollowUpForm({ workOrder, jobTypes, close, saved }: any) {
  const preferred =
    jobTypes.find((x: any) => x.code === "PPM_FOLLOW_UP") || jobTypes[0];
  const [form, setForm] = useState({
    jobTypeId: preferred?.id || "",
    title: `Follow-up from ${workOrder.number}: ${workOrder.title}`,
    description: "",
    priority: "MEDIUM",
  });
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  return (
    <div className="cmms-modal" role="dialog" aria-modal="true">
      <form
        className="cmms-temp-form"
        onSubmit={async (e: FormEvent) => {
          e.preventDefault();
          setBusy(true);
          setError("");
          try {
            const result = await post(
              `/api/maintenance/work-orders/${workOrder.id}/ppm-follow-up`,
              form,
            );
            saved(result.number);
          } catch (err: any) {
            setError(err.message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <div>
          <h2>Create PPM follow-up</h2>
          <p>
            This creates a linked normal work order for a defect or additional
            task found during the PPM.
          </p>
        </div>
        <label>
          Job type
          <select
            required
            value={form.jobTypeId}
            onChange={(e) => setForm({ ...form, jobTypeId: e.target.value })}
          >
            {jobTypes.map((x: any) => (
              <option key={x.id} value={x.id}>
                {x.name}
                {x.contractor ? " — contractor" : ""}
              </option>
            ))}
          </select>
        </label>
        <label>
          Follow-up title
          <input
            required
            minLength={3}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </label>
        <label>
          Work required
          <textarea
            required
            rows={5}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </label>
        <label>
          Priority
          <select
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: e.target.value })}
          >
            <option>LOW</option>
            <option>MEDIUM</option>
            <option>HIGH</option>
            <option>PRODUCTION_CAN_RUN</option>
            <option>PRODUCTION_STOPPED</option>
          </select>
        </label>
        {error ? <p className="cmms-error">{error}</p> : null}
        <div className="cmms-modal-actions">
          <button type="button" className="secondary" onClick={close}>
            Cancel
          </button>
          <button disabled={busy}>
            {busy ? "Creating…" : "Create linked follow-up"}
          </button>
        </div>
      </form>
    </div>
  );
}

function TemporaryRepairForm({ close, save }: any) {
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const [temporaryFix, setTemporaryFix] = useState("");
  const [workRequired, setWorkRequired] = useState("");
  const [partsRequired, setPartsRequired] = useState("");
  const [permanentRepairDueAt, setPermanentRepairDueAt] = useState(tomorrow);
  const [photo, setPhoto] = useState<File | undefined>();
  const [busy, setBusy] = useState(false);
  return (
    <div className="cmms-modal" role="dialog" aria-modal="true">
      <form
        className="cmms-temp-form"
        onSubmit={async (event: FormEvent) => {
          event.preventDefault();
          setBusy(true);
          await save(
            {
              temporaryFix,
              workRequired,
              partsRequired,
              permanentRepairDueAt,
            },
            photo,
          );
          setBusy(false);
        }}
      >
        <div>
          <h2>Record temporary repair</h2>
          <p>
            Record enough detail for another engineer to safely complete the
            permanent repair without relying on a verbal handover.
          </p>
        </div>
        <label>
          Temporary repair made <b>*</b>
          <textarea
            required
            minLength={5}
            rows={4}
            value={temporaryFix}
            onChange={(e) => setTemporaryFix(e.target.value)}
            placeholder="Describe exactly what was fitted, adjusted, isolated or bypassed and how the machine was left safe."
          />
        </label>
        <label>
          Permanent work still required <b>*</b>
          <textarea
            required
            minLength={5}
            rows={5}
            value={workRequired}
            onChange={(e) => setWorkRequired(e.target.value)}
            placeholder="Describe the remaining diagnosis, removal, repair, replacement, testing and reinstatement work."
          />
        </label>
        <label>
          Parts, materials or specialist support required
          <textarea
            rows={3}
            value={partsRequired}
            onChange={(e) => setPartsRequired(e.target.value)}
            placeholder="Include part descriptions, codes, quantities or supplier action if known."
          />
        </label>
        <label>
          Permanent repair due <b>*</b>
          <input
            required
            type="date"
            min={tomorrow}
            value={permanentRepairDueAt}
            onChange={(e) => setPermanentRepairDueAt(e.target.value)}
          />
        </label>
        <label>
          Supporting photo
          <input
            type="file"
            accept="image/jpeg,image/png"
            onChange={(e) => setPhoto(e.target.files?.[0])}
          />
          <small>
            Optional, maximum 2 MB. Avoid personal or confidential information.
          </small>
        </label>
        <div className="cmms-modal-actions">
          <button type="button" className="secondary" onClick={close}>
            Cancel
          </button>
          <button disabled={busy}>
            {busy ? "Saving…" : "Record temporary repair"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ActionButtons({
  w,
  run,
  openAction,
  openClaim,
  openTemporaryForm,
  canComplete,
  canSignoff,
}: any) {
  if (w.status === "OPEN")
    return (
      <button onClick={openClaim}>
        <UserPlus />
        Claim for myself
      </button>
    );
  if (!canComplete) return null;
  if (["CLAIMED", "ASSIGNED", "PAUSED", "AWAITING_PARTS"].includes(w.status))
    return (
      <button onClick={() => run("start")}>
        <Play />
        Start / resume work
      </button>
    );
  if (w.status === "IN_PROGRESS")
    return (
      <>
        <button
          onClick={() => openAction("pause", "Pause work", "reason", "Reason for pausing")}
        >
          Pause
        </button>
        <button
          onClick={() => openAction("await-parts", "Record parts required", "reason", "Parts or materials required")}
        >
          Awaiting parts
        </button>
        <button onClick={openTemporaryForm}>Record temporary fix</button>
        <button
          onClick={() =>
            openAction(
              "permanent-repair",
              "Record permanent repair",
              "permanentRepair",
              "Permanent repair completed",
            )
          }
        >
          <Wrench />
          Permanent repair complete
        </button>
      </>
    );
  if (w.status === "TEMPORARY_FIX")
    return (
      <button
        onClick={() =>
          openAction(
            "permanent-repair",
            "Record permanent repair",
            "permanentRepair",
            "Permanent repair completed",
          )
        }
      >
        <Wrench />
        Permanent repair complete
      </button>
    );
  if (w.status === "AWAITING_SIGNOFF" && canSignoff)
    return (
      <button
        onClick={() => run("signoff", { comment: "Checked and accepted" })}
      >
        Sign off work
      </button>
    );
  if (w.status === "COMPLETED" && canSignoff)
    return (
      <button
        onClick={() =>
          openAction("reopen", "Reopen completed work order", "reason", "Reason for reopening")
        }
      >
        Reopen
      </button>
    );
  return null;
}
